/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { IAny, ICoreContext, IGeneric, IImageSettings } from '@msdyn365-commerce/core';
// tslint:disable-next-line: no-duplicate-imports
import MsDyn365 from '@msdyn365-commerce/core';
import { AttributeValue, CommerceProperty, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import get from 'lodash/get';
import uniqBy from 'lodash/uniqBy';
import * as React from 'react';
import { InView } from 'react-intersection-observer';
import { IMFICartLine as IMFICartLineBatch } from '../../../actions/BatchCoreProductDataServiceEntities.g';
import { IMFIItem, IMFIPromotion } from '../../../actions/EyebrowMessageDataServiceEntities.g';
import { GetCategoryPathsInput, getProductsCategoryPathsAction } from '../../../actions/get-mfrm-products-category-paths.action';
import { getBrandNameCheck } from '../../../Utilities/get-product-brand';
import {
    _coreProductATPCall,
    _getEyebrowMessage,
    _getSoonestDeliveryDate,
    _makeDeliveryMessage
} from '../../../Utilities/product-search-result-utils';
import { IMfrmSearchResultContainerResources } from '../../../modules/mfrm-search-result-container/mfrm-search-result-container.props.autogenerated';
import { ProductComponent } from '../../../themes/mattressfirm/views/components/product.component';
import { getPagePath } from '../../../Utilities/get-page-path';
import getListOfCoreDeliveryMessage, { IMFIATPInventoryEntityPLPEx } from '../../../Utilities/get-core-delivery-batch';
import { _getProductType } from '../../../themes/mattressfirm/views/components/delivery-message/deliverymessage.component';
import { ErrorBoundary } from '../../../common/components/error-boundary';
export interface ProductSearchResultExtended extends ProductSearchResult {
    eyebrowMessage?: string;
    deliveryByMessage?: string;
}

export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
interface IProductSearchResultItems {
    products: ProductSearchResult[];
    onlyNewProducts: ProductSearchResult[];
    currentPromoProducts: ProductSearchResult[];
    context: ICoreContext<IGeneric<IAny>>;
    resources: IMfrmSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    productBadgeAttribute?: string;
    mattressMatcherBanner?: React.ReactNode;
    bannerPosition?: number;
    mattressMatcherBannerIndex?: number;
    targetAudienceGroup?: string;
    listName?: string;
    isCategoryPage: boolean;
    categoryNameFromSlug: string | undefined;
    isRefinersEnabled?: boolean;
    showBvRatings?: boolean;
}
interface IFetchedCategoryIds {
    productId?: number;
    categoryId?: number;
    categoryName?: string;
    brand: string;
    variantID: string | number | undefined;
}

const getProductsCategories = async (
    context: ICoreContext<IGeneric<IAny>>,
    products: ProductSearchResult[] | undefined,
    isCategoryPage: boolean,
    categoryNameFromSlug: string | undefined
): Promise<IFetchedCategoryIds[]> => {
    if (products && products.length > 0) {
        // VSI Customization Start - 42859
        let catName: string | undefined = '';
        if (isCategoryPage) {
            catName = categoryNameFromSlug;
        }
        // VSI Customization End - 42859
        const productIds: number[] = [];
        const catalogId = context.actionContext.requestContext.apiSettings.catalogId;
        for (let productPricesIter = 0;productPricesIter < products.length;productPricesIter++) {
            const prodId = products[productPricesIter].RecordId;
            productIds.push(prodId);
        }
        const productsCategories = await getProductsCategoryPathsAction(
            new GetCategoryPathsInput(context.request, catalogId, productIds),
            context.actionContext
        );
        const categoryIds: IFetchedCategoryIds[] = [];
        for (let productCategoriesIter = 0;productCategoriesIter < productsCategories.length;productCategoriesIter++) {
            if (
                productsCategories[productCategoriesIter] &&
                productsCategories[productCategoriesIter].CategoryPath &&
                productsCategories[productCategoriesIter].CategoryPath!.length > 0
            ) {
                const catPath = productsCategories[productCategoriesIter].CategoryPath;
                const catPathSegments = catPath![0].Segments;
                if (catPathSegments && catPathSegments.length > 0) {
                    const catId = catPathSegments.length > 0 ? catPathSegments[catPathSegments.length - 1].CategoryId : -1;
                    if (!isCategoryPage) {
                        catName = catPathSegments.length > 0 ? catPathSegments[catPathSegments.length - 1].CategoryName : '';
                    }
                    const collectionProductAttributes: AttributeValue[] = products[productCategoriesIter].AttributeValues!;
                    let variantId: string | number | undefined;
                    const configVendorNamePlp = context.app.config.brandBackofficeAttributePlp;
                    const configVendorNamePdp = context.app.config.brandBackofficeAttributePdp;
                    let brandName = getBrandNameCheck(collectionProductAttributes, configVendorNamePlp);
                    if (!brandName) {
                        brandName = getBrandNameCheck(collectionProductAttributes, configVendorNamePdp);
                    }
                    if (!brandName) {
                        brandName = 'Default brand name';
                    }
                    const eProps: CommerceProperty[] = products[productCategoriesIter].ExtensionProperties!;
                    if (eProps && eProps.length > 0) {
                        for (let ePropsIter = 0;ePropsIter < eProps.length;ePropsIter++) {
                            if (eProps[ePropsIter].Key === 'Unbxd_DefaultVariantRetailVariantId') {
                                variantId = eProps[ePropsIter].Value?.StringValue;
                                break;
                            }
                        }
                    }

                    categoryIds.push({
                        productId: productsCategories[productCategoriesIter].ProductId,
                        categoryId: catId,
                        categoryName: catName,
                        brand: brandName,
                        variantID: variantId
                    });
                }
            }
        }
        return categoryIds;
    }
    return [];
};

const getSkipProductValue = (context: ICoreContext<IGeneric<IAny>>): number => {
    let skipProducts: number = 0;
    const viewPortCode = getViewport(context);
    if (viewPortCode === 'xs' || viewPortCode === 'sm' || viewPortCode === 'md') {
        skipProducts = 1;
    } else if (viewPortCode === 'lg') {
        if (MsDyn365.isBrowser && window.innerWidth) {
            if (window.innerWidth >= 1070 && window.innerWidth < 1112) {
                skipProducts = 2;
            } else {
                skipProducts = 3;
            }
        }
    } else if (viewPortCode === 'xl') {
        skipProducts = 3;
    }
    return skipProducts;
};

const getViewport = (context: ICoreContext<IGeneric<IAny>>): GridSize => {
    // always render in mobile viewport on a mobile device
    if (context.request && context.request.device && context.request.device.Type === 'Mobile') {
        return 'xs';
    }
    if (MsDyn365.isBrowser && window.innerWidth) {
        const gridSettings = context.request.gridSettings;
        if (gridSettings) {
            if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                return 'xs';
            } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                return 'sm';
            } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                return 'md';
            } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                return 'lg';
            } else {
                return 'xl';
            }
        }
    }

    return 'lg';
};

const callTelium = (
    listName: string | undefined,
    products: ProductSearchResult[] | undefined,
    categoryID: IFetchedCategoryIds[],
    productIDList: number[]
): void => {
    const listNameValue = listName ? `${listName}` : 'PLP';
    const pagePath = getPagePath();
    const categoryListArray = categoryID.filter(item1 => productIDList.some(item2 => item1.productId === item2));
    const categoryListArr: string[] = [];
    const brandNameArr: string[] = [];
    const productVariantArr: (string | number)[] = [];
    categoryListArray.map((item: IFetchedCategoryIds) => {
        categoryListArr.push(item.categoryName!);
        brandNameArr.push(item.brand);
        productVariantArr.push(item.variantID!);
    });
    const productNameArr: string[] = [];
    const productIdArr: string[] = [];
    const productPositionArr: number[] = [];
    const productPriceArr: number[] = [];
    const productListArray =
        products &&
        products.filter((item1, index) => {
            return productIDList.some(item2 => {
                const productID = item1.RecordId;
                if (productID === item2) {
                    productPositionArr.push(index + 1);
                }
                return productID === item2; // return the ones with equal id
            });
        });
    productListArray &&
        productListArray.map((item: ProductSearchResult, index: number) => {
            productNameArr.push(item.Name!);
            productIdArr.push(item.ItemId!);
            productPriceArr.push(item.Price);
        });

    // @ts-ignore
    if (MsDyn365.isBrowser && 'utag' in window) {
        // @ts-ignore
        // tslint:disable-next-line: object-literal-key-quotes
        utag.link({
            event: 'product-impression',
            event_category: 'ecommerce',
            event_action: 'product impression',
            enhanced_action: 'product_impression',
            event_label: `${pagePath}`,
            event_noninteraction: 'true',
            product_impression_list_name: `${pagePath}` === '/search' ? 'SRP' : listNameValue,
            product_impression_position: productPositionArr,
            product_impression_id: productIdArr,
            product_impression_name: productNameArr,
            product_impression_category: categoryListArr,
            product_impression_brand: brandNameArr,
            product_impression_variant: productVariantArr,
            product_impression_price: productPriceArr
        });
    }
};

const callTeliumOnClick = (categoryID: IFetchedCategoryIds[], product: ProductSearchResult, listName: string | undefined): void => {
    const pagePath = getPagePath();
    const listNameValue = listName ? `${listName}` : 'Standard PLP';
    const productName = product.Name;
    const productPositionArr: number[] = [];
    const productRecordID = product.RecordId;
    const categoryListArr: string[] = [];
    const brandNameArr: (string | undefined)[] = [];
    const productVariantArr: (string | number)[] = [];
    const productId = product.ItemId;
    categoryID?.forEach((item, index) => {
        if (productRecordID === item.productId) {
            productPositionArr.push(index + 1);
            categoryListArr.push(item.categoryName!);
            brandNameArr.push(item.brand);
            productVariantArr.push(item.variantID!);
        }
    });
    // @ts-ignore
    if ('utag' in window) {
        // @ts-ignore
        // tslint:disable-next-line: object-literal-key-quotes
        utag.link({
            event: 'product-click',
            enhanced_action: 'product_click',
            event_category: 'ecommerce',
            event_action: 'product click',
            event_label: `${productId}: ${productName}`,
            event_noninteraction: 'false',
            product_list_name: `${pagePath}` === '/search' ? 'SRP' : listNameValue,
            product_position: productPositionArr,
            product_id: [`${productId}`],
            product_name: [`${productName}`],
            product_category: categoryListArr,
            product_brand: brandNameArr,
            product_variant: productVariantArr
        });
    }
};

const updateProductsMessages = (
    products: ProductSearchResultExtended[],
    resultEyebrow: IMFIPromotion[] | null,
    targetAudienceGroup: string | undefined
) => {
    products.map((product: ProductSearchResultExtended) => {
        if (resultEyebrow && resultEyebrow !== undefined) {
            let eyebrowMessage: string | undefined = '';
            if (resultEyebrow !== undefined) {
                const resultEyebrowTemp = resultEyebrow !== undefined ? resultEyebrow.slice() : [];
                resultEyebrowTemp.length &&
                    resultEyebrowTemp.map((item: IMFIPromotion) => {
                        if (product.ItemId === item.ItemId) {
                            if (item.EyeBrows && item.EyeBrows.length && item.EyeBrows.length > 0) {
                                if (targetAudienceGroup) {
                                    for (const eyebrow of item.EyeBrows) {
                                        const targetAudienceGroupValue = eyebrow.Description?.split('|')[2]?.toLowerCase();
                                        if (targetAudienceGroupValue === targetAudienceGroup.toLowerCase()) {
                                            eyebrowMessage = eyebrow.Description?.split('|')[0];
                                            break;
                                        }
                                    }
                                    // If no value configured then show the first EyebrowMessage without audienceGroup
                                } else {
                                    for (const eyebrow of item.EyeBrows) {
                                        if (eyebrow.Description?.split('|').length === 2) {
                                            eyebrowMessage = eyebrow.Description?.split('|')[0];
                                            break;
                                        }
                                    }
                                }
                            }
                        }
                    });
                product.eyebrowMessage = eyebrowMessage;
            }
        }
    });
};

const updateCoreDeiveryMessage = (products: ProductSearchResultExtended[],
    deliveryByMessages: IMFIATPInventoryEntityPLPEx[] | undefined) => {
    deliveryByMessages && deliveryByMessages?.length && products?.length && products.map(item => {
        const DistinctProductVariant = item.IsMasterProduct ?
            item.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue :
            item.RecordId.toString();
        const defaultVariantId = item.ExtensionProperties && item.ExtensionProperties.find(
            (property: CommerceProperty) => property?.Key === "Unbxd_DefaultVariantID"
        )?.Value?.LongValue;
        const recId = item.IsMasterProduct ? defaultVariantId?.toString() ?? DistinctProductVariant : item.RecordId?.toString();
        item.deliveryByMessage = deliveryByMessages?.find(message => message.VariantRecordId === recId)?.deliveryMessage ?? '';
    });
};

// tslint:disable-next-line:max-func-body-length
export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    productBadgeAttribute,
    mattressMatcherBanner,
    bannerPosition,
    mattressMatcherBannerIndex,
    targetAudienceGroup,
    listName,
    currentPromoProducts,
    isCategoryPage,
    categoryNameFromSlug,
    onlyNewProducts,
    isRefinersEnabled,
    showBvRatings
}) => {
    const [resultEyebrow, setResultEyebrow] = React.useState<IMFIPromotion[] | null>(null);
    const [resultCoreDelivery, setResultCoreDelivery] = React.useState<IMFIATPInventoryEntityPLPEx[] | undefined>(undefined);
    const [productIDList, setproductIDList] = React.useState<number[]>([]);
    const [categoryID, setCategoryID] = React.useState<IFetchedCategoryIds[]>([]);
    const productsForCallingAPI = onlyNewProducts?.length > 0 ? onlyNewProducts : currentPromoProducts;
    React.useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getProductsCategories(context, productsForCallingAPI, isCategoryPage, categoryNameFromSlug).then(result => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            setCategoryID(uniqBy([...categoryID, ...result], 'productId'));
        });

        if (currentPromoProducts?.length) {
            callATPDelivery();
        }
    }, [productsForCallingAPI]);
    if (currentPromoProducts?.length) {
        const productIdsArray: IMFIItem[] | undefined = [];
        // const coreItems: IMFICartLine[] = [];
        productsForCallingAPI.map((item: ProductSearchResultExtended) => {
            // currentPromoProducts.map((item: IMessages) => {
            const DistinctProductVariant = item.IsMasterProduct ?
                item.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue :
                item.RecordId.toString();
            const defaultVariantId = item.ExtensionProperties && item.ExtensionProperties.find(
                (property: CommerceProperty) => property?.Key === "Unbxd_DefaultVariantID"
            )?.Value?.LongValue;
            const eyebrowMessageObj: IMFIItem = {
                ItemId: item.ItemId,
                DistinctProductVariant: defaultVariantId?.toString() ?? DistinctProductVariant
            };
            productIdsArray.push(eyebrowMessageObj);

        });
        const eyebrowMessagePromise = _getEyebrowMessage(productIdsArray, context);
        eyebrowMessagePromise
            .then(result => {
                if (result !== undefined) {
                    setResultEyebrow(result);
                }
            })
            .catch(e => {
                console.log('Error', e);
            });
        updateProductsMessages(productsForCallingAPI, resultEyebrow, targetAudienceGroup);
        resultCoreDelivery && updateCoreDeiveryMessage(productsForCallingAPI, resultCoreDelivery);
    }

    const callATPDelivery = () => {
        const cartLines: IMFICartLineBatch[] = [];
        // const coreItems: IMFICartLine[] = [];
        productsForCallingAPI.map((item: ProductSearchResultExtended) => {
            // currentPromoProducts.map((item: IMessages) => {
            const DistinctProductVariant = item.IsMasterProduct ?
                item.AttributeValues?.find(attributeValue => attributeValue.Name === "EcomLowestPriceVariantRecId")?.TextValue :
                item.RecordId.toString();
            const defaultVariantId = item.ExtensionProperties && item.ExtensionProperties.find(
                (property: CommerceProperty) => property?.Key === "Unbxd_DefaultVariantID"
            )?.Value?.LongValue;
            const shippingInformationSmall = item.AttributeValues && item.AttributeValues.find(
                (attr: AttributeValue) => attr.Name?.trim() === context.app.config.smallParcelAttributeKey
            )?.TextValue?.toLowerCase();
            const defaultVariantShippingInformation = item.ExtensionProperties && item.ExtensionProperties.find(
                (property: CommerceProperty) => property?.Key === "Unbxd_DefaultVariantShippingInformation"
            )?.Value?.StringValue;
            if ((_getProductType(defaultVariantShippingInformation ?? shippingInformationSmall)) === 'Core') {
                const recId = item.IsMasterProduct ? defaultVariantId?.toString() ?? DistinctProductVariant : item.RecordId?.toString();
                cartLines.push({ ItemId: item.ItemId, Quantity: 1, VariantRecordId: recId });
            }
        });
        if (cartLines?.length) {
            void getListOfCoreDeliveryMessage(context.actionContext, cartLines).then(response => {
                if (response !== undefined) {
                    setResultCoreDelivery(response);
                }
            }).catch(e => {
                console.log('Error [getListOfCoreDeliveryMessage]', e);
            });
        }
    };

    const handleProductId = (productID: number): void => {
        setproductIDList(prevArray => [...prevArray, productID]);
    };
    const skipProductsVal = getSkipProductValue(context);
    if (productIDList.length > skipProductsVal - 1) {
        if (categoryID.length > 0) {
            callTelium(listName, products, categoryID, productIDList);
            setproductIDList([]);
        }
    }
    // tslint:disable: no-shadowed-variable
    const renderProducts = (
        context: ICoreContext<IGeneric<IAny>>,
        resources: IMfrmSearchResultContainerResources,
        moduleType: string,
        moduleId: string,
        telemetryContent: ITelemetryContent,
        products: ProductSearchResult[],
        imageSettings?: IImageSettings,
        allowBackNavigation?: boolean,
        productBadgeAttribute?: string
    ) => {
        const [productArray, setProductArray] = React.useState<number[]>([]);
        const checkInvew = (inView: boolean, product: ProductSearchResultExtended) => {
            const productID = product.RecordId;
            const checkId = productArray.includes(productID);
            if (!checkId) {
                if (inView) {
                    setProductArray(prevArray => [...prevArray, productID]);
                    handleProductId(productID);
                }
            }
        };
        return (
            <ul className='list-unstyled'>
                {products.map((product: ProductSearchResultExtended, index: number) => (
                    // tslint:disable-next-line: react-this-binding-issue jsx-no-lambda
                    <InView
                        onClick={() => callTeliumOnClick(categoryID, product, listName)}
                        as='div'
                        rootMargin='0px 0px -200px 0px'
                        onChange={inView => checkInvew(inView, product)}
                        key={index}
                    >
                        <li className='ms-product-search-result__item' key={index}>
                            <ErrorBoundary key={index} telemetry={context.telemetry}>
                                <ProductComponent
                                    context={context}
                                    telemetryContent={telemetryContent}
                                    imageSettings={imageSettings}
                                    freePriceText={product.BasePrice ? resources.priceFree : ''}
                                    originalPriceText={resources.originalPriceText}
                                    currentPriceText={resources.currentPriceText}
                                    ratingAriaLabel={resources.ratingAriaLabel}
                                    allowBack={allowBackNavigation}
                                    id={moduleId}
                                    typeName={moduleType}
                                    data={{ product: product }}
                                    eyebrowMessage={product.eyebrowMessage}
                                    badgeAttribute={productBadgeAttribute}
                                    deliveryMessage={product.deliveryByMessage}
                                    isRefinersEnabled={isRefinersEnabled}
                                    showBvRatings={showBvRatings}
                                />
                            </ErrorBoundary>
                        </li>
                    </InView>
                ))}
            </ul>
        );
    };
    const shouldShowMattressMatcherBanner =
        mattressMatcherBanner !== undefined && mattressMatcherBannerIndex !== undefined && mattressMatcherBannerIndex >= 0;
    return (
        <>
            {bannerPosition !== undefined && bannerPosition >= 0 ? (
                <>
                    {renderProducts(
                        context,
                        resources,
                        moduleType,
                        moduleId,
                        telemetryContent,
                        products.slice(0, bannerPosition),
                        imageSettings,
                        allowBackNavigation,
                        productBadgeAttribute
                    )}
                    {shouldShowMattressMatcherBanner ? (
                        <div data-m-image-position={get(mattressMatcherBanner![mattressMatcherBannerIndex!], 'props.config.imagePosition')}>
                            {mattressMatcherBanner![mattressMatcherBannerIndex!]}
                        </div>
                    ) : (
                        <></>
                    )}
                    {renderProducts(
                        context,
                        resources,
                        moduleType,
                        moduleId,
                        telemetryContent,
                        products.slice(bannerPosition, products.length),
                        imageSettings,
                        allowBackNavigation,
                        productBadgeAttribute
                    )}
                </>
            ) : (
                <>
                    {renderProducts(
                        context,
                        resources,
                        moduleType,
                        moduleId,
                        telemetryContent,
                        products,
                        imageSettings,
                        allowBackNavigation,
                        productBadgeAttribute
                    )}
                </>
            )}
        </>
    );
};
